import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Button,
  Chip,
  Card,
  CardContent,
  styled
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import { getOfferById } from '../../services/offerService';
import { toast } from 'react-toastify';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'accepted':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

const DetailItem = ({ label, value }) => (
  <Box mb={1.5}>
    <Typography sx={{
      fontSize: '0.72rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'text.secondary',
      fontWeight: 600,
      mb: 0.5
    }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: 'text.primary' }}>
      {value}
    </Typography>
  </Box>
);

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #f6f9ff 0%, #ffffff 60%)',
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4)
  }
}));

const ContentWrapper = styled(Container)(({ theme }) => ({
  maxWidth: '1400px !important',
  padding: 0,
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0, 1)
  }
}));

const MainCard = styled(Card)(({ theme }) => ({
  background: '#fff',
  borderRadius: '28px',
  border: '1px solid rgba(16, 24, 40, 0.06)',
  boxShadow: '0 20px 40px rgba(16, 24, 40, 0.08)',
  overflow: 'hidden'
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(6),
  '&:last-child': {
    paddingBottom: theme.spacing(6)
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8),
    '&:last-child': {
      paddingBottom: theme.spacing(8)
    }
  }
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  background: '#fff',
  borderRadius: '20px',
  border: '1px solid rgba(16, 24, 40, 0.06)',
  boxShadow: '0 6px 20px rgba(16, 24, 40, 0.06)'
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2.5, 3),
  borderBottom: '1px solid rgba(16, 24, 40, 0.06)'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '1.05rem',
  fontWeight: 700,
  letterSpacing: '-0.01em'
}));

const FieldGroup = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr'
  }
}));

const OfferDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const offerFromLoan = location.state?.offerFromLoan;
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOfferDetails = async () => {
      try {
        const response = await getOfferById(id);
        if (response?.statusCode === 200) {
          const fetchedRaw = response.result;
          const core = fetchedRaw?.result ?? fetchedRaw;
          const mergedCore = offerFromLoan ? { ...core, ...offerFromLoan } : core;
          const mergedWithBank = {
            ...mergedCore,
            bankType: fetchedRaw?.bankType ?? mergedCore?.bankType,
            bankName: fetchedRaw?.bankName ?? mergedCore?.bankName,
          };
          setOffer(mergedWithBank);
        } else {
          toast.error('Failed to fetch offer details');
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOfferDetails();
  }, [id, offerFromLoan]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!offer) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h6" color="error">
          Offer not found
        </Typography>
      </Container>
    );
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/offers')}
            sx={{ mb: 2 }}
          >
            Back to Offers
          </Button>
        </Box>

        <MainCard>
          <StyledCardContent>
            <Paper sx={{
              p: 4,
              mb: 3,
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%)',
              border: '1px solid rgba(16, 24, 40, 0.06)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: { xs: '1.4rem', md: '1.8rem' }, fontWeight: 800, letterSpacing: '-0.02em' }}>
                    Offer Details
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {`Offer ID: OFFER-${offer.id}`}
                  </Typography>
                </Box>
                <Chip label={offer.offerStatus} color={getStatusColor(offer.offerStatus)} sx={{ fontWeight: 700 }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccountBalanceIcon sx={{ color: 'primary.main' }} />
                <Typography sx={{ color: 'text.secondary' }}>Bank</Typography>
                <Typography sx={{ fontWeight: 700 }}>{offer.bankName || 'N/A'}</Typography>
                <Typography sx={{ color: 'text.secondary', mx: 1 }}>•</Typography>
                <Typography sx={{ color: 'text.secondary' }}>Type</Typography>
                <Typography sx={{ fontWeight: 700 }}>{offer.bankType || 'N/A'}</Typography>
              </Box>
            </Paper>

            <SectionCard sx={{ mb: 3 }}>
              <SectionHeader>
                <AccountBalanceIcon sx={{ color: 'primary.main' }} />
                <SectionTitle>Financial Summary</SectionTitle>
              </SectionHeader>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Loan Details</Typography>
                    <FieldGroup>
                      <DetailItem label="Loan Amount" value={formatCurrency(offer.loanOffered)} />
                      <DetailItem label="Interest Rate" value={`${offer.rateOfIntrest}%`} />
                      <DetailItem label="Repayment Tenure" value={`${offer.repaymentTenure} months`} />
                      <DetailItem label="Processing Fees" value={formatCurrency(offer.processingFees)} />
                    </FieldGroup>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Additional Details</Typography>
                    <FieldGroup>
                      <DetailItem label="Miscellaneous Charges" value={formatCurrency(offer.miscellaneousCharges)} />
                      <DetailItem label="Security Coverage" value={`${offer.securityCoveragePercent}%`} />
                      <DetailItem label="Lender ID" value={offer.lenderId} />
                      <DetailItem label="Borrower ID" value={offer.borrowerId || 'Not assigned'} />
                      <DetailItem label="Loan Breakdown" value={offer.loanBreakDown} />
                    </FieldGroup>
                  </Grid>
                </Grid>
              </Box>
            </SectionCard>

            <SectionCard sx={{ mb: 3 }}>
              <SectionHeader>
                <AssignmentIcon sx={{ color: 'primary.main' }} />
                <SectionTitle>Audit Information</SectionTitle>
              </SectionHeader>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <DetailItem label="Created By" value={offer.createOpName} />
                    <DetailItem label="Created Date" value={new Date(offer.createDatetime).toLocaleString('en-IN')} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailItem label="Last Updated By" value={offer.commitOpName} />
                    <DetailItem label="Last Updated" value={new Date(offer.commitDatetime).toLocaleString('en-IN')} />
                  </Grid>
                </Grid>
              </Box>
            </SectionCard>

            <SectionCard>
              <SectionHeader>
                <GavelOutlinedIcon sx={{ color: 'primary.main' }} />
                <SectionTitle>Decision Details</SectionTitle>
              </SectionHeader>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  {(offer.acceptedBy || offer.acceptedReason || offer.acceptedDate || offer.acceptedByID) ? (
                    <Grid item xs={12}>
                      <FieldGroup>
                        <DetailItem label="Accepted By" value={offer.acceptedBy || 'N/A'} />
                        <DetailItem label="Accepted By ID" value={offer.acceptedByID || 'N/A'} />
                        <DetailItem label="Accepted Date" value={offer.acceptedDate ? new Date(offer.acceptedDate).toLocaleString('en-IN') : 'N/A'} />
                        <DetailItem label="Accepted Reason" value={offer.acceptedReason || 'N/A'} />
                      </FieldGroup>
                    </Grid>
                  ) : (
                    (offer.rejectedBy || offer.rejectedReason || offer.rejectedDate || offer.rejectedByID) ? (
                      <Grid item xs={12}>
                        <FieldGroup>
                          <DetailItem label="Rejected By" value={offer.rejectedBy || 'N/A'} />
                          <DetailItem label="Rejected By ID" value={offer.rejectedByID || 'N/A'} />
                          <DetailItem label="Rejected Date" value={offer.rejectedDate ? new Date(offer.rejectedDate).toLocaleString('en-IN') : 'N/A'} />
                          <DetailItem label="Rejected Reason" value={offer.rejectedReason || 'N/A'} />
                        </FieldGroup>
                      </Grid>
                    ) : (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">No decision recorded</Typography>
                      </Grid>
                    )
                  )}
                </Grid>
              </Box>
            </SectionCard>
          </StyledCardContent>
        </MainCard>
      </ContentWrapper>
    </PageContainer>
  );
};

export default OfferDetails; 